import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { Store } from "../store";
import billingService, { BillingStatusResponse } from "../services/billing";

interface BillingContextType {
  loading: boolean;
  isLocked: boolean;
  showWarning: boolean;
  daysRemaining: number;
  outstandingInvoice: any | null;
  refreshStatus: () => Promise<void>;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const authState = useSelector((state: Store) => state.auth);
  const [status, setStatus] = useState<BillingStatusResponse>({
    isLocked: false,
    showWarning: false,
    daysRemaining: 0,
    outstandingInvoice: null,
  });
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    // Don't fetch if not logged in
    if (!authState.token) return;

    try {
      setLoading(true);
      const response = await billingService.getStatus();
      if (response) {
        setStatus(response);
      }
    } catch (error) {
      console.error("Failed to fetch billing status", error);
      // Fail gracefully (don't lock out on network error)
    } finally {
      setLoading(false);
    }
  }, [authState.token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return (
    <BillingContext.Provider
      value={{ ...status, loading, refreshStatus: fetchStatus }}
    >
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error("useBilling must be used within a BillingProvider");
  }
  return context;
};
